// tabs/prepare-spells.ts — client renderer for the PC sheet's Prepare-Spells tab.
//
// L7b (2026-09-27): the (class, list) table is CLIENT TRUTH. janus-engine/spells/spell-prep-table.ts builds it from the caster
// row this page holds (prepared map, spells_known, at-will, class levels, essences, prepare_additional_*) plus the class-pool
// catalog payload (options/api/spell_prep_pool_inc.php, one request per page — janus-engine/spells/spell-prep-pool.ts): the caps
// (spell-prep-limits.ts), the spell list, the known/prepared flags. Each spell carries its lean `nest`; the cells read
// sphere/circle/range/aoe/duration/casting-time/charges from it via ftLayerValue (mirrors spell_prepared_tr_inc.php).
//
// State (active class + list) is held in a closure; class/list pills + prepare/unprepare buttons are
// wired by ONE delegated click listener on the container. Prepare/unprepare are CLIENT TRUTH (L7, 2026-09-26): the
// gates run here (janus-engine/spells/prepared-spells.ts, PHP's refusal text) and the write commits to
// spell_prep_truth_commit_inc.php (authority + identity + CAS + patched caches + a frame to every viewer;
// janus-engine/spells/spell-prep-commit.ts). On success we toggle
// just that one row's Prepare cell + the in-memory limits/counters; a spinner/status line by the
// List pills reports progress. A List/class switch rebuilds the table locally. Reset/Update commit the same way
// (commitSpellPrepList: switch gated on combat / Quick Study / arcane threshold) for THIS tab's caster.

import type { CachedCaster, CasterItemNode } from '../types';
import { resolveNest, scalarLayerSlot } from '../../shared/array-types';
import { getSpellTimeShortLabel } from '../../shared/spell-casting-time';
import { rangeIcon, displayAoeCompact, displayDuration, chargeAbilityIcon } from '../../shared/spell-display';
import { decodeHtmlEntitiesForPlainText } from '../../shared/decode-html-entities';
import { countPrepared, isPrepared, normalizePreparedMap, preparedIds, resolvePrepare, type PreparedMap, type Verdict } from '../../janus-engine/spells/prepared-spells';
import { commitSpellPrep, commitSpellPrepList, type SpellPrepTruth } from '../../janus-engine/spells/spell-prep-commit';
import { buildPrepTable, poolKeysForRow, type PoolPayload, type PrepClass, type PrepSpell as TablePrepSpell, type PrepTable } from '../../janus-engine/spells/spell-prep-table';
import { refuseUnported } from '../../janus-engine/unported';
import { heldPrepPool, loadPrepPool } from '../../janus-engine/spells/spell-prep-pool';
import { catalogNameById } from './ability-row';

type PrepSpell = TablePrepSpell & { nest: CasterItemNode };
type PrepPayload = PrepTable & { spells: PrepSpell[] };


const sanitizeId = (s: string): string => String(s).replace(/[^A-Za-z0-9_-]/g, '_');
const stripTags = (s: string): string => String(s).replace(/<[^>]*>/g, '').trim();
function num(v: unknown, fb = 0): number { const n = Number(Array.isArray(v) ? v[0] : v); return Number.isFinite(n) ? n : fb; }

// Inject once: cap the spell-table's height so ONLY the rows scroll, and pin the column
// headers (sticky thead) — the List/class pills, limit chips and filter pills all live above
// the scroll box, so they stay "freeze-framed" on top while you scroll/filter the spell list.
function ensurePrepStyles(): void {
    if (document.getElementById('ft-prep-spells-styles')) return;
    const el = document.createElement('style');
    el.id = 'ft-prep-spells-styles';
    el.textContent = `
.ft-pc-prepare-spells .prepared-spells-scroll{max-height:62vh;overflow-y:auto;}
.ft-pc-prepare-spells .prepared-spells-scroll thead th{position:sticky;top:0;z-index:3;background:var(--bs-body-bg,#fff);box-shadow:inset 0 -1px 0 var(--bs-border-color,#dee2e6);}`;
    document.head.appendChild(el);
}

// ── cells (read the lean nest, mirror spell_prepared_tr_inc.php) ──
function runeInline(nest: CasterItemNode): string {
    let json = 'null';
    try { json = JSON.stringify({ items: nest?.items ?? {}, item_layer_types: nest?.item_layer_types ?? {}, catalog: nest?.catalog ?? {} }); } catch { json = 'null'; }
    json = json.replace(/<\//g, '<\\/');
    return `<div class="ft-rune-mount ft-rune-inline" data-rune-size="22"><script type="application/json" class="ft-rune-blob">${json}</script></div>`;
}
function sphereCell(nest: CasterItemNode): string {
    if (!ftLayerHas(nest, 'spell_sphere')) return '';
    // Entries are bucketed shells ({items:{item_id}}), not bare UUIDs — String(s) would
    // stringify the object and iconMap falls back to the "?" icon. scalarLayerSlot is the
    // TS twin of PHP item_arr_scalar_layer_slot (what the legacy row template relied on).
    return ftLayerEntries(nest, 'spell_sphere').map((s: unknown) => iconMap(scalarLayerSlot(s))).join('');
}
function classIconsCell(nest: CasterItemNode, layer: string): string {
    if (!ftLayerHas(nest, layer)) return '';
    return ftLayerEntries(nest, layer).map((c: unknown) => iconMap(scalarLayerSlot(c), 40, 40)).join('');
}
function specialChargesCell(nest: CasterItemNode): string {
    let out = '';
    const consumes = ftLayerValue(nest, 'consumes_ability_charges');
    if (consumes !== null && consumes !== undefined) {
        const qty = ftLayerValue(nest, 'consumes_charges_qty');
        if (qty !== null && qty !== undefined) out += `<span class="badge bg-dark border me-1">${escapeHtml(String(qty))}</span>`;
        out += chargeAbilityIcon(consumes);
    }
    const adds = ftLayerValue(nest, 'add_charges_to_ability');
    if (adds !== null && adds !== undefined) {
        const qty = ftLayerValue(nest, 'add_charges_qty');
        if (qty !== null && qty !== undefined) out += `<span class="badge bg-dark border me-1">${escapeHtml(String(qty))}</span>`;
        out += chargeAbilityIcon(adds);
    }
    return out;
}
function castingTimeCell(nest: CasterItemNode): string {
    let out = '';
    if (ftLayerHas(nest, 'spell_ritual')) out += iconMap('spell_ritual');
    const t = String(ftLayerValue(nest, 'spell_casting_time') ?? '');
    if (t === '3ap') out += iconMap('cast_3ap_spell');
    else if (t === '2ap') out += iconMap('cast_2ap_spell');
    else if (t === '1ap') out += iconMap('cast_1ap_spell');
    else if (t === 'reaction') out += iconMap('cast_reaction_spell');
    // Long cast times: bespoke cast_3ap_spell icon stays; the short label is sourced from the shared
    // single-source map (canonical form, e.g. "1 Hr"/"8 Hrs" — was the divergent " 1 hr"/" 8 hrs").
    else { const short = getSpellTimeShortLabel(t); if (short) out += iconMap('cast_3ap_spell') + '<br />' + short; }
    return out;
}
function rangeCell(nest: CasterItemNode): string {
    return rangeIcon(nest);
}
function aoeCell(nest: CasterItemNode): string {
    return displayAoeCompact(nest) || '—';
}
function durationCell(nest: CasterItemNode): string {
    let out = '';
    if (ftLayerHas(nest, 'spell_concentration')) out += iconMap('concentration');
    const d = displayDuration(nest, true);
    if (d) out += (out ? ' ' : '') + d;
    return out;
}

function prepareCell(o: PrepSpell, p: PrepPayload): string {
    if (o.known === 'at_will') return `<button class="btn btn-sm btn-info" disabled>${iconMap('at_will_spell')}</button>`;
    if (o.known === 'spell') return `<button class="btn btn-sm btn-info" disabled>${iconMap('spell_known')}</button>`;
    if (o.prepared) {
        return `<button type="button" class="btn btn-sm btn-danger" data-ft-prep-action="unprepare" data-spell-id="${escapeHtml(o.id)}"${p.can_edit ? '' : ' disabled'}>${iconMap('unprepare_spell')}</button>`;
    }
    const limitDisabled = o.level === 0 ? p.cantrip_button_disabled : p.spells_button_disabled;
    const disabled = !p.can_edit || limitDisabled;
    return `<button type="button" class="btn btn-sm btn-primary" data-ft-prep-action="prepare" data-spell-id="${escapeHtml(o.id)}"${disabled ? ' disabled' : ''}>${iconMap('prepare_spell')}</button>`;
}

function spellRow(o: PrepSpell, p: PrepPayload): string {
    const cls = `spells_prepared_tr spells_prepared_tr_${o.level}${o.concentration ? ' spells_prepared_tr_concentration' : ''}${o.ritual ? ' spells_prepared_tr_ritual' : ''}`;
    return `<tr class="${cls}" data-spell-name="${escapeHtml(o.name.toLowerCase())}" data-spell-level="${o.level}" data-spell-known="${o.known !== null ? 1 : 0}" data-spell-prepared="${o.prepared ? 1 : 0}">
        <td class="text-center">${prepareCell(o, p)}</td>
        <td><div class="d-flex align-items-center">${runeInline(o.nest)}<span role="button" class="link-primary" onclick="openItemInfo('${escapeHtml(o.id)}');">${escapeHtml(o.name)}</span></div></td>
        <td class="text-center">${o.level}</td>
        <td class="text-center">${sphereCell(o.nest)}</td>
        <td class="text-center">${iconMap(String(ftLayerValue(o.nest, 'spell_circle') ?? ''))}</td>
        <td class="text-center">${classIconsCell(o.nest, 'requires_class')}</td>
        <td class="text-center">${classIconsCell(o.nest, 'requires_subclass')}</td>
        <td class="text-center text-nowrap">${specialChargesCell(o.nest)}</td>
        <td class="text-center text-nowrap">${castingTimeCell(o.nest)}</td>
        <td class="text-center">${rangeCell(o.nest)}</td>
        <td class="text-center text-nowrap">${aoeCell(o.nest)}</td>
        <td class="text-right text-nowrap">${durationCell(o.nest)}</td>
    </tr>`;
}

// The level/concentration/ritual pills are single-select (a radio set sharing data-ft-prep-filter).
// The trailing Known/Prepared pills are INDEPENDENT toggles (data-ft-prep-toggle): each, when active,
// restricts the table to that category, they union with each other, and AND with the radio + search.
// Their active state is driven by the passed-in closure flags so it survives a list/class reload.
function filterPills(p: PrepPayload, knownOnly: boolean, preparedOnly: boolean): string {
    if (p.max_spell_level_castable < 0) return '';
    const pill = (label: string, mode: string, level = ''): string =>
        `<li class="nav-item p-1" role="presentation"><button class="nav-link border${mode === 'all' ? ' active' : ''}" type="button" data-ft-prep-filter="${mode}" data-ft-prep-level="${level}">${label}</button></li>`;
    const togglePill = (label: string, toggle: string, on: boolean): string =>
        `<li class="nav-item p-1" role="presentation"><button class="nav-link border${on ? ' active' : ''}" type="button" data-ft-prep-toggle="${toggle}">${label}</button></li>`;
    let pills = pill('All', 'all');
    for (let x = 0; x <= 9; x++) if (p.max_spell_level_castable >= x) pills += pill(String(x), 'level', String(x));
    pills += pill(iconMap('concentration'), 'concentration');
    pills += pill(iconMap('spell_ritual'), 'ritual');
    pills += `<li class="nav-item p-1 d-flex align-items-center" role="presentation"><span class="vr"></span></li>`;
    pills += togglePill(`${iconMap('spell_known', 25, 25, 'Show only spells you already know')} Known`, 'known', knownOnly);
    pills += togglePill(`${iconMap('prepare_spell', 25, 25, 'Show only spells you have already prepared')} Prepared`, 'prepared', preparedOnly);
    return `<ul class="nav nav-pills flex-wrap mb-0" role="tablist">${pills}</ul>`;
}

// Compact gradient "stat chips" for the cantrip/spell limits. Rendered up on the
// class-pills row (right-aligned) so they fill that row's empty band instead of
// eating their own. The count spans keep their original IDs/structure (just a
// number inside) because side-panels-init.js live-updates them via `.innerHTML = n`.
// Cool blue (cantrips) vs warm fuchsia (spells) keeps the two visually distinct.
function limitChipsHtml(p: PrepPayload): string {
    const chip = (
        infoId: string, label: string, preparedId: string, prepared: number,
        maxId: string, max: number, gradient: string,
    ): string => `<div class="d-inline-flex align-items-center gap-2 rounded-pill px-3 py-2"
        style="background:${gradient};box-shadow:0 1px 4px rgba(0,0,0,.45);">
        <span role="button" class="d-inline-flex align-items-center" onclick="openItemInfo('${infoId}')">${iconMap('info')}</span>
        <span class="fw-semibold text-white text-nowrap">${label}</span>
        <span id="${preparedId}" class="badge bg-light text-dark">${prepared}</span>
        <span class="text-white-50 fw-bold">/</span>
        <span id="${maxId}" class="badge bg-dark text-light border border-light">${max}</span>
    </div>`;
    return `${chip('D4AE681B-577B-3B61-4821-719CBBBC6789', 'Cantrips Known', 'span_class_cantrips_prepared', p.limits.cantrips_prepared, 'max_class_cantrips_known', p.limits.max_cantrips, 'linear-gradient(135deg,#1d4ed8,#0ea5e9)')}
        ${chip('4543E8FC-FC29-451A-A691-C3E2700094C3', 'Max Spells Known', 'span_class_spells_prepared', p.limits.spells_prepared, 'max_class_spells_known', p.limits.max_spells, 'linear-gradient(135deg,#be185d,#f472b6)')}`;
}

function tableHtml(p: PrepPayload, searchId: string, knownOnly: boolean, preparedOnly: boolean): string {
    // Search box mirrors the inventory/ability search (spyglass pinned to the trailing
    // edge, pe-4 reserves room). The glyph is INLINED rather than the `#search` sprite
    // because this tab mounts where the same-document sprite `<use>` doesn't resolve —
    // same self-contained stroke spyglass the site navbar search uses. Fixed #555c64
    // stroke (NOT text-muted): the input is light, but the theme's muted color is light
    // too, so a themed color renders invisible on the white field (documented trap).
    const search = `<div class="ms-sm-auto position-relative" style="max-width:14rem;width:100%;">
        <input type="search" id="${searchId}" class="form-control form-control-sm pe-4 w-100" placeholder="Search spells…" autocomplete="off">
        <svg class="position-absolute top-50 end-0 translate-middle-y me-2" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555c64" stroke-width="2.25" stroke-linecap="round" aria-hidden="true" focusable="false" style="pointer-events:none;"><circle cx="11" cy="11" r="7"/><path d="M16 16l4.5 4.5"/></svg>
    </div>`;
    const rows = p.spells.map((o) => spellRow(o, p)).join('');
    return `
    <div class="prepared-spells-table-wrap" data-filter-mode="all" data-filter-level="">
        <div class="d-flex flex-wrap align-items-center gap-2 my-2">
            ${filterPills(p, knownOnly, preparedOnly)}
            ${search}
        </div>
        <div class="table-responsive-xxl prepared-spells-scroll pl-1 pr-1 pb-2 pt-0 mb-3"><table class="table table-hover table-sm">
            <thead><tr>
                <th class="text-center" style="width:60px;">Prepare</th>
                <th class="text-left">${iconMap('spell')}</th>
                <th class="text-center" style="width:30px;">${iconMap('spell_level')}</th>
                <th class="text-center" style="width:130px;">${iconMap('sphere_of_magic')}</th>
                <th class="text-center" style="width:30px;">${iconMap('circle_of_magic')}</th>
                <th class="text-center" style="width:30px;">${iconMap('requires_class')}</th>
                <th class="text-center" style="width:30px;">${iconMap('requires_subclass')}</th>
                <th class="text-center" style="width:70px;">${iconMap('charges')}</th>
                <th class="text-center" style="width:60px;">${iconMap('spell_casting_time')}</th>
                <th class="text-center" style="width:30px;">${iconMap('range')}</th>
                <th class="text-center" style="width:60px;">AOE</th>
                <th class="text-center" style="width:150px;">${iconMap('duration')}</th>
            </tr></thead>
            <tbody>${rows}</tbody>
        </table></div>
    </div>`;
}

function classPillsHtml(classes: PrepClass[], activeNum: number): string {
    const pills = classes.map((c) =>
        `<li class="nav-item p-1" role="presentation"><button class="nav-link border rounded${c.class_number === activeNum ? ' active' : ''}" type="button" data-ft-prep-class="${c.class_number}">${iconMap(c.icon_id)} ${escapeHtml(c.name)}</button></li>`,
    ).join('');
    return `<ul class="nav nav-pills" role="tablist">${pills}</ul>`;
}

function listPillsHtml(activeList: number): string {
    const pill = (n: number) => `<li class="nav-item p-1" role="presentation"><button class="nav-link border${n === activeList ? ' active' : ''}" type="button" data-ft-prep-list="${n}">List ${n}</button></li>`;
    return `<ul class="nav nav-pills" role="tablist">${pill(1)}${pill(2)}${pill(3)}</ul>`;
}

export function renderPrepareSpells(caster: CachedCaster, casterId: string, container: HTMLElement): void {
    // Idempotent guard. Surgical caster_update re-renders call paintPane on EVERY mounted
    // pane. Truth frames update prepared flags below; a generic WS tick must leave the pane (and any
    // already-loaded table) untouched — never wipe or re-fetch on re-render.
    if (container.querySelector('.ft-pc-prepare-spells')) return;
    ensurePrepStyles();

    const ecArr = caster.encounter_creatures ?? {};
    const state = { class_number: 0, spell_list: num(ecArr.spell_list_prepared, 1) || 1 };
    // The commit keys on the ENCOUNTER row (prepared rows are stored under it); for a PC it is usually the item id too.
    const rawEcId: unknown = Array.isArray(ecArr.encounter_creature_id) ? ecArr.encounter_creature_id[0] : ecArr.encounter_creature_id;
    const ecId = String(rawEcId ?? '') || casterId;
    const liveRow = (): Record<string, unknown> =>
        (((window as unknown as { FateTide?: { casterCache?: Map<string, unknown> } }).FateTide?.casterCache?.get(casterId)) ?? caster) as Record<string, unknown>;
    const rootId = `ft_prepare_${sanitizeId(casterId)}`;
    const searchId = `${rootId}_search`;

    container.innerHTML = `<div class="ft-pc-prepare-spells" id="${rootId}">
        <div class="d-flex flex-wrap align-items-center gap-2 mt-1 mb-2">
            <div data-ft-prep-list-pills>${listPillsHtml(state.spell_list)}</div>
            <div data-ft-prep-status class="small text-muted text-nowrap" aria-live="polite"></div>
            <div class="ms-sm-auto d-flex flex-wrap align-items-center gap-2">
                <button type="button" class="btn btn-danger border" data-ft-prep-cmd="reset">Reset Spell List</button>
                <button type="button" class="btn btn-warning border" data-ft-prep-cmd="update">Update Prepared Spells</button>
            </div>
        </div>
        <input id="input_hidden_prepared_spell_list" type="hidden" value="${state.spell_list}">
        <input id="input_hidden_prepared_spell_class_number" type="hidden" value="">
        <div class="mt-1 mb-2" data-ft-prep-class-pills></div>
        <div data-ft-prep-table><div class="text-center p-4 text-muted">Choose <strong>List 1</strong>, <strong>2</strong>, or <strong>3</strong> above to load your prepared spells.</div></div>
        <div id="div_refresh_prepared_spells" class="d-none"></div>
    </div>`;

    const root = container.querySelector(`#${CSS.escape(rootId)}`) as HTMLElement | null;
    if (!root) return;
    const classPillsBox = root.querySelector('[data-ft-prep-class-pills]') as HTMLElement;
    const tableBox = root.querySelector('[data-ft-prep-table]') as HTMLElement;
    const listHidden = root.querySelector('#input_hidden_prepared_spell_list') as HTMLInputElement;
    const classHidden = root.querySelector('#input_hidden_prepared_spell_class_number') as HTMLInputElement;
    const statusBox = root.querySelector('[data-ft-prep-status]') as HTMLElement | null;

    // Status line sitting to the right of the List pills: spinner while a prepare/unprepare
    // POST is in flight, then a brief success/error note (server's own rejection text on failure).
    function showPrepStatus(kind: 'spin' | 'ok' | 'err', msg = ''): void {
        if (!statusBox) return;
        if (kind === 'spin') statusBox.innerHTML = `<span class="spinner-border spinner-border-sm align-middle me-1" role="status" aria-hidden="true"></span><span class="align-middle">Updating…</span>`;
        else if (kind === 'ok') statusBox.innerHTML = `<span class="text-success">✓ ${escapeHtml(msg)}</span>`;
        else statusBox.innerHTML = `<span class="text-danger">⚠ ${escapeHtml(msg)}</span>`;
    }

    let current: PrepPayload | null = null;
    let currentPool: PoolPayload | null = null;

    // The raw pool is held once for the page. Class/list changes build locally;
    // a late response cannot paint over a newer class/list choice.
    let loadGeneration = 0;
    async function load(): Promise<void> {
        const generation = ++loadGeneration;
        let payload: PrepPayload;
        try {
            // Only the page's first load waits on the network; afterwards the payload is held and the table builds at once. The
            // row may move on while a pool loads (a class change arrives by frame) — re-key against the refreshed row (Codex review).
            let row = liveRow();
            let pool: PoolPayload | null = heldPrepPool(poolKeysForRow(row));
            for (let attempt = 0; !pool && attempt < 3; attempt++) {
                tableBox.innerHTML = `<div class="text-center p-4 text-muted">Loading prepared spells…</div>`;
                const keys = poolKeysForRow(row);
                const loaded = await loadPrepPool(keys, BASE_URL);
                if (generation !== loadGeneration) return;
                row = liveRow();
                pool = poolKeysForRow(row).join(',') === keys.join(',') ? loaded : heldPrepPool(poolKeysForRow(row));
            }
            if (!pool) throw new Error('spell prep pool: the row kept changing');
            currentPool = pool;
            payload = buildPrepTable({
                row, payload: pool, classNumber: state.class_number, spellList: state.spell_list,
                canEdit: typeof ftHasPlayerPermission === 'function' ? ftHasPlayerPermission(row) : false,
                deps: {
                    resolveDef: (id) => {
                        const full = (window as unknown as { ftFindFullNestedItem?: (id: string) => unknown }).ftFindFullNestedItem?.(id);
                        return full ?? resolveNest(id, row);
                    },
                    nameOf: (id) => catalogNameById(id),
                },
            });
        } catch (error) {
            if (generation !== loadGeneration) return;
            current = null; currentPool = null;
            const reason = error instanceof Error ? error.message : 'spell-prep:inputs-unavailable';
            refuseUnported(reason);
            tableBox.innerHTML = `<div class="text-center p-4 text-danger">Failed to load prepared spells (${escapeHtml(reason)}) — click a List above to retry.</div>`;
            return;
        }
        if (!payload || payload.error) {
            classPillsBox.innerHTML = '';
            tableBox.innerHTML = `<div class="text-center p-4 text-muted">No caster classes.</div>`;
            return;
        }
        // items.item_name / class names are persisted HTML-ENCODED in the DB ("Curse of Death&#039;s
        // Inevitability"), and the API hands them over verbatim. escapeHtml() on that re-encodes the
        // entity's & (&#039; → &amp;#039;) and the literal "&#039;" shows on screen. Decode ONCE here,
        // at ingest, so every downstream consumer (row name, class pills, the lowercased
        // data-spell-name the search filter matches against, the status line) gets true plain text and
        // escapeHtml stays the sole encoder. Mirrors inventory.ts / render-object-card.ts.
        (payload.spells ?? []).forEach((s) => { s.name = decodeHtmlEntitiesForPlainText(s.name); });
        (payload.classes ?? []).forEach((c) => { c.name = decodeHtmlEntitiesForPlainText(c.name); });
        current = payload;
        state.class_number = payload.class_number;
        state.spell_list = payload.spell_list;
        classHidden.value = String(payload.class_number);
        listHidden.value = String(payload.spell_list);
        classPillsBox.innerHTML = `<div class="d-flex flex-wrap align-items-center gap-2">
            ${classPillsHtml(payload.classes, payload.class_number)}
            <div class="ms-sm-auto d-flex flex-wrap align-items-center gap-2">${limitChipsHtml(payload)}</div>
        </div>`;
        tableBox.innerHTML = tableHtml(payload, searchId, showKnownOnly, showPreparedOnly);
        window.ftMountRuneIcons?.(tableBox);
        applyFilter();
    }

    // ── client-side filter (mode + known/prepared toggles + search), mirrors the legacy pill/search behaviour ──
    let filterMode = 'all', filterLevel = '';
    let showKnownOnly = false, showPreparedOnly = false;
    function applyFilter(): void {
        const searchEl = root!.querySelector(`#${CSS.escape(searchId)}`) as HTMLInputElement | null;
        const term = (searchEl?.value ?? '').trim().toLowerCase();
        tableBox.querySelectorAll<HTMLElement>('tr.spells_prepared_tr').forEach((tr) => {
            let show = true;
            if (filterMode === 'level') show = tr.getAttribute('data-spell-level') === filterLevel;
            else if (filterMode === 'concentration') show = tr.classList.contains('spells_prepared_tr_concentration');
            else if (filterMode === 'ritual') show = tr.classList.contains('spells_prepared_tr_ritual');
            // Known/Prepared toggles: each active toggle is a "show only" constraint; when both are on
            // they union (a row matching EITHER passes), then AND with the radio mode + search above.
            if (show && (showKnownOnly || showPreparedOnly)) {
                const isKnown = tr.getAttribute('data-spell-known') === '1';
                const isPrepared = tr.getAttribute('data-spell-prepared') === '1';
                show = (showKnownOnly && isKnown) || (showPreparedOnly && isPrepared);
            }
            if (show && term) show = (tr.getAttribute('data-spell-name') ?? '').includes(term);
            tr.style.display = show ? '' : 'none';
        });
    }

    // ── optimistic prepare/unprepare toggle (no full table re-fetch) ──
    // Keep the limit-count chips (live-updated elsewhere by id) in sync with current.limits.
    function updateChipCounts(): void {
        if (!current) return;
        const c = root!.querySelector('#span_class_cantrips_prepared');
        const s = root!.querySelector('#span_class_spells_prepared');
        if (c) c.innerHTML = String(current.limits.cantrips_prepared);
        if (s) s.innerHTML = String(current.limits.spells_prepared);
    }
    // After a count change, re-evaluate every still-unprepared button's disabled state against
    // the (now-updated) per-tier caps so hitting a limit greys out the rest immediately.
    function refreshPrepareButtons(): void {
        if (!current) return;
        tableBox.querySelectorAll<HTMLButtonElement>('button[data-ft-prep-action="prepare"]').forEach((b) => {
            const lvl = Number(b.closest('tr')?.getAttribute('data-spell-level') ?? '0');
            const limitDisabled = lvl === 0 ? current!.cantrip_button_disabled : current!.spells_button_disabled;
            b.disabled = !current!.can_edit || limitDisabled;
        });
    }

    root.addEventListener('click', (e) => {
        const t = (e.target as HTMLElement)?.closest('[data-ft-prep-list],[data-ft-prep-class],[data-ft-prep-action],[data-ft-prep-filter],[data-ft-prep-toggle],[data-ft-prep-cmd]') as HTMLElement | null;
        if (!t || !root.contains(t)) return;

        const listN = t.getAttribute('data-ft-prep-list');
        if (listN !== null) {
            state.spell_list = Number(listN); listHidden.value = listN;
            root.querySelector('[data-ft-prep-list-pills]')!.innerHTML = listPillsHtml(state.spell_list);
            void load();
            return;
        }
        const classN = t.getAttribute('data-ft-prep-class');
        if (classN !== null) { state.class_number = Number(classN); void load(); return; }

        const action = t.getAttribute('data-ft-prep-action');
        if (action && current && !(t as HTMLButtonElement).disabled) {
            const btn = t as HTMLButtonElement;
            const spellId = btn.getAttribute('data-spell-id') ?? '';
            const spell = current.spells.find((s) => s.id === spellId);
            const isPrepare = action === 'prepare';
            if (!spell) return;
            // L7 (2026-09-26): the browser decides (prepare_spell_inc.php's gates, PHP's text) and commits to
            // spell_prep_truth_commit_inc.php. The table's native readers already resolved
            // the selected spell's level, including saved private/deleted definitions.
            // Everything the decision depends on is CAPTURED at the click (Codex S2 client review #3): switching the viewed
            // list / class mid-flight must not change what is gated or sent.
            const payload = current;
            const list = state.spell_list;
            const classId = payload.class_id;
            // Saved archived/deleted spells still consume their real level's slots. Their ids
            // need not appear in the currently displayed class pool.
            const levels = new Map(Object.entries(currentPool?.index ?? {}).map(([id, value]) => [id.toUpperCase(), Number(value[0] ?? 0)] as const));
            for (const spell of payload.spells) if (!levels.has(spell.id.toUpperCase())) levels.set(spell.id.toUpperCase(), spell.level);
            // The gate runs against the EXACT truth each POST carries (commitSpellPrep calls it before the first send and
            // before the one retry after a stale answer) — counts come from that map, never from the displayed payload.
            const gate = (truth: SpellPrepTruth): Verdict => {
                const already = isPrepared(truth.prepared, classId, list, spellId);
                if (isPrepare === already) return { ok: false, message: `${spell.name} is already ${isPrepare ? 'prepared' : 'removed'}.` };
                if (!isPrepare) return { ok: true };
                const n = countPrepared(truth.prepared, classId, list, (id) => levels.get(id));
                return resolvePrepare({
                    level: spell.level, featOk: true, // native pool readers already drop feat-gated spells
                    cantripsPrepared: n.cantrips, maxCantrips: payload.limits.max_cantrips,
                    spellsPrepared: n.spells, maxSpells: payload.limits.max_spells,
                });
            };
            btn.disabled = true;
            showPrepStatus('spin');
            void commitSpellPrep({
                casterId: ecId, action: isPrepare ? 'prepare' : 'unprepare', spellList: list,
                classId, spellId, classNumber: payload.class_number, row: liveRow(), gate,
            }).then((out) => {
                if (out.ok) {
                    // Reconcile from the NEWEST truth this page knows (a later write from another tab may already have
                    // landed), never by toggling to what this — possibly older — operation asked for (review #5).
                    if (current === payload && state.spell_list === list) syncFromMap(out.truth.prepared);
                    showPrepStatus('ok', `${spell.name} ${isPrepare ? 'prepared' : 'removed'}`);
                } else {
                    showPrepStatus('err', stripTags(out.message) || 'Could not update — try again.');
                    btn.disabled = false;
                    refreshPrepareButtons();
                }
            }).catch(error => {
                const reason = error instanceof Error ? error.message : 'spell-prep:definition-unavailable';
                refuseUnported(reason);
                showPrepStatus('err', reason);
                btn.disabled = false;
                refreshPrepareButtons();
            });
            return;
        }

        const filter = t.getAttribute('data-ft-prep-filter');
        if (filter !== null) {
            filterMode = filter; filterLevel = t.getAttribute('data-ft-prep-level') ?? '';
            tableBox.querySelectorAll('[data-ft-prep-filter]').forEach((b) => b.classList.remove('active'));
            t.classList.add('active');
            applyFilter();
            return;
        }

        const toggle = t.getAttribute('data-ft-prep-toggle');
        if (toggle !== null) {
            if (toggle === 'known') showKnownOnly = !showKnownOnly;
            else if (toggle === 'prepared') showPreparedOnly = !showPreparedOnly;
            t.classList.toggle('active');
            applyFilter();
            return;
        }

        const cmd = t.getAttribute('data-ft-prep-cmd');
        if (cmd === 'reset' || cmd === 'update') {
            // L7 S3: Reset (every class's rows of the VIEWED list) / Update (make the VIEWED list the active one; gated on
            // combat / Quick Study and the arcane threshold; one Arcane Fatigue stack in a campaign) commit directly —
            // this tab's own caster, never the side-panel's "active controller".
            const list = state.spell_list;
            const btn = t as HTMLButtonElement;
            btn.disabled = true;
            showPrepStatus('spin');
            const classNumber = current?.class_number ?? state.class_number;
            void commitSpellPrepList({ casterId: ecId, action: cmd === 'reset' ? 'reset' : 'switch', list, classNumber,
                className: current?.classes.find(c => c.class_number === classNumber)?.name, row: liveRow() })
                .then((out) => {
                    btn.disabled = false;
                    if (!out.ok) { showPrepStatus('err', stripTags(out.message) || 'Could not update — try again.'); return; }
                    showPrepStatus('ok', cmd === 'reset' ? `Spell List ${list} Reset` : `List ${list} is now your prepared list`);
                    void load();
                });
            return;
        }
    });

    // A committed prepared-spell write from ANY tab (websocket/spell-prep-truth.ts re-dispatches the frame): re-derive the
    // loaded (class, list) table's prepared flags + counts from the frame's map. Idempotent for this tab's own writes.
    window.addEventListener('ft:spell-prep-truth', (ev) => {
        const cmd = (ev as CustomEvent<{ actor_id?: string; sections?: { prepared?: unknown; spell_list_prepared?: unknown } }>).detail;
        if (!current || !root.isConnected || String(cmd?.actor_id ?? '').toUpperCase() !== ecId.toUpperCase()) return;
        syncFromMap(normalizePreparedMap(cmd.sections?.prepared ?? null));
    });
    // The loaded (class, list) table ← a prepared map: flags per row; counts exactly as build_known_spells_numbered_inc.php
    // counts them (every prepared id of the list, by its level; an id outside the table counts as a spell).
    function syncFromMap(map: PreparedMap): void {
        if (!current) return;
        const ids = new Set(preparedIds(map, current.class_id, state.spell_list).map((id) => id.toUpperCase()));
        const levels = new Map(current.spells.map((s) => [s.id.toUpperCase(), s.level] as const));
        const { cantrips, spells } = countPrepared(map, current.class_id, state.spell_list, (id) => levels.get(id));
        for (const s of current.spells) {
            const now = s.known === null && ids.has(s.id.toUpperCase());
            if (now === s.prepared) continue;
            s.prepared = now;
            const tr = tableBox.querySelector(`button[data-spell-id="${CSS.escape(s.id)}"]`)?.closest('tr') as HTMLElement | null;
            const cell = tr?.querySelector('td');
            if (cell) cell.innerHTML = prepareCell(s, current);
            tr?.setAttribute('data-spell-prepared', now ? '1' : '0');
        }
        current.limits.cantrips_prepared = cantrips;
        current.limits.spells_prepared = spells;
        current.cantrip_button_disabled = cantrips >= current.limits.max_cantrips;
        current.spells_button_disabled = spells >= current.limits.max_spells;
        updateChipCounts();
        refreshPrepareButtons();
        applyFilter();
    }

    root.addEventListener('input', (e) => {
        if ((e.target as HTMLElement)?.id === searchId) applyFilter();
    });

    // The pane mounts as an inert shell; the first explicit
    // click on a List (1/2/3) pill loads the pool once for the page (`void load()`); every later pill click builds
    // locally. Once loaded it sticks (the idempotency guard at the top keeps WS re-renders from wiping it).
}
